
import { defineComponent } from 'vue'

// presentation information
interface PresentationInfo {
  title: string,
  author: string,
  conference: string,
  location: string,
  date: string,
  number?: string,
  note?: string
}

export default defineComponent({
  data () {
    return {
      presentations: [
        {
          title: 'Altermagnetism-induced spatially nonuniform superconductivity',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          conference: 'The 30th International Conference on Low Temperature Physics',
          location: 'Bilbao Exhibition Centre (Bilbao, Spain)',
          date: 'August 2025',
          number: 'P40'
        },
        {
          title: 'Phase-modulated superconductivity via altermagnetism',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          conference: 'International Workshop on Chirality in Condensed Matter Physics',
          location: 'Institute of Physics, Academia Sinica (Taipei, Taiwan)',
          date: 'June 2025',
          number: 'P16'
        },
        {
          title: 'Interplay between altermagnetic order and finite-momentum superconductivity',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          conference: 'The 23rd Korea-Taiwan-Japan Symposium on Strongly Correlated Electron System',
          location: 'Pusan National University (Busan, South Korea)',
          date: 'March 2025',
          number: 'P19'
        },
        {
          title: 'Interplay between altermagnetic order and spatially nonuniform superconductivity',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          conference: 'Workshop on theory of cross correlations, multipoles, and computational material design',
          location: 'Gotemba Tokinosumika (Shizuoka, Japan)',
          date: 'February 2025',
          number: '15'
        },
        {
          title: 'Spin parity effect in an anisotropic Heisenberg chain under a transverse magnetic field',
          author: '<u>Shuntaro Sumita</u>, Akihiro Tanaka, and Yusuke Kato',
          conference: 'The 22nd International Conference on Magnetism',
          location: 'Bologna Congress Center (Bologna, Italy)',
          date: 'July 2024',
          number: '#ICM2024_2.07_983'
        },
        {
          title: 'Anisotropy-induced spin parity effects',
          author: '<u>Shuntaro Sumita</u>, Akihiro Tanaka, and Yusuke Kato',
          conference: 'Trends in the Theory of Quantum Materials 2024',
          location: 'University of Fribourg (Fribourg, Switzerland)',
          date: 'June 2024',
          number: '7'
        },
        {
          title: 'Fulde–Ferrell–Larkin–Ovchinnikov State Induced by Antiferromagnetic Order in κ-type Organic Conductors',
          author: '<u>Shuntaro Sumita</u>, Makoto Naka, and Hitoshi Seo',
          conference: 'International Conference on Strongly Correlated Electron Systems 2023',
          location: 'Songdo ConvensiA (Incheon, Korea)',
          date: 'July 2023',
          number: '8-0441',
          note: '<b class="text-success">Best Poster Award</b>'
        },
        {
          title: 'Supercurrent-induced Weyl superconductivity',
          author: '<u>Shuntaro Sumita</u> and Kazuaki Takasan',
          conference: '29th International Conference on Low Temperature Physics',
          location: 'Sapporo Convention Center (Hokkaido, Japan)',
          date: 'August 2022',
          number: 'P23-SF2A-22'
        },
        {
          title: 'Classification of topological crystalline nodal superconductivity',
          author: '<u>Shuntaro Sumita</u>',
          conference: 'Trends in Theory of Correlated Materials 2019',
          location: 'Kyoto Univ. (Kyoto, Japan)',
          date: 'October 2019',
          number: 'P13'
        },
        {
          title: 'Topological Gapless Superconductivity with Rotation Symmetry',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, Ken Shiozaki, and Youichi Yanase',
          conference: 'International Conference on Strongly Correlated Electron Systems 2019',
          location: 'Okayama Convention Center (Okayama, Japan)',
          date: 'September 2019',
          number: 'Tu-L-04'
        },
        {
          title: 'Topological classification of superconducting nodes on rotational axes',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, Ken Shiozaki, and Youichi Yanase',
          conference: 'Spectroscopies in Novel Superconductors 2019',
          location: 'The Univ. of Tokyo (Tokyo, Japan)',
          date: 'June 2019',
          number: 'P-Th-17'
        },
        {
          title: 'Topological classification of symmetry-protected superconducting point nodes',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, Ken Shiozaki, and Youichi Yanase',
          conference: 'Trends in Theory of Correlated Materials 2018',
          location: 'Uni Dufour (Genève, Switzerland)',
          date: 'October 2018',
          number: '5'
        },
        {
          title: 'Unconventional superconducting gap structure protected by space group symmetry',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          conference: '12th International Conference on Materials and Mechanisms of Superconductivity',
          location: 'Beijing International Convention Center (Beijing, China)',
          date: 'August 2018',
          number: 'Th-49'
        },
        {
          title: 'Weyl superconductivity depending on angular momentum',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          conference: 'TMS-EPiQS 2nd Alliance Workshop: Topological magnets and topological superconductors',
          location: 'Kyoto Univ. (Kyoto, Japan)',
          date: 'January 2018',
          number: 'P-14'
        },
        {
          title: 'Multipole superconductivity in nonsymmorphic Sr<sub>2</sub>IrO<sub>4</sub>',
          author: '<u>Shuntaro Sumita</u>, Takuya Nomoto, and Youichi Yanase',
          conference: 'International Conference on Strongly Correlated Electron Systems 2017',
          location: 'Clarion Congress Hotel (Prague, Czech Republic)',
          date: 'July 2017',
          number: 'Tu-7-50'
        },
        {
          title: 'Superconductivity in magnetic multipole state',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          conference: 'International Conference on Strongly Correlated Electron Systems 2016',
          location: 'Zhejiang Univ. (Hangzhou, China)',
          date: 'May 2016',
          number: 'Tu-P066'
        },
        {
          title: 'Fulde-Ferrell-Larkin-Ovchinnikov state induced by magnetic quadrupole order in zigzag chains',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          conference: 'CEMS Topical Meeting on Emergent 2D Materials',
          location: 'RIKEN (Saitama, Japan)',
          date: 'December 2015',
          number: 'P17'
        },
        {
          title: 'Fulde-Ferrell-Larkin-Ovchinnikov state in zigzag chains',
          author: '<u>Shuntaro Sumita</u> and Youichi Yanase',
          conference: 'International Symposium on Present and Future of Material Sciences',
          location: 'Osaka Univ. (Osaka, Japan)',
          date: 'November 2015',
          number: '44'
        }
      ] as PresentationInfo[]
    }
  }
})
